import { css, Global } from '@emotion/react';
import { Types, useRouteInfo } from '@scorenco/core';
import { PropsWithChildren, useMemo } from 'react';
import { pickTextColorBasedOnBgColorSimple } from '../../utils';
import { Box } from '@mui/material';

type WidgetsGlobalStylesProps = PropsWithChildren<{
  widget?: Types.Widgets_Widget;
}>;

export const WidgetsGlobalStyles = ({
  widget,
  children,
}: WidgetsGlobalStylesProps) => {
  const routeInfo = useRouteInfo();
  const paramColor =
    routeInfo.query.color && routeInfo.query.color !== 'undefined'
      ? decodeURIComponent(routeInfo.query.color as string)
      : undefined;
  const paramDisableLinks =
    routeInfo.query.disable_links &&
    routeInfo.query.disable_links !== 'undefined'
      ? decodeURIComponent(routeInfo.query.disable_links as string)
      : undefined;

  const backgroundColor = widget?.settings?.background_color;
  const primaryColor = paramColor || widget?.settings?.color;
  const secondaryColor = paramColor || widget?.settings?.color;
  const fontColor = widget?.settings?.font_color;
  const disableLinks = paramDisableLinks === 'true';

  const styles = useMemo(() => {
    return {
      globals: css`
        ${widget?.custom_css}

        ${primaryColor &&
        css`
          --snc-palette-primary-main: ${primaryColor};
          --snc-palette-primary-contrastText: ${pickTextColorBasedOnBgColorSimple(
            primaryColor,
            '#ffffff',
            '#000000'
          )};
        `}

      ${fontColor &&
        css`
          --snc-palette-text-primary: ${fontColor};
          --snc-palette-text-secondary: ${fontColor};
        `}

      ${backgroundColor &&
        css`
          --snc-palette-background-paper: ${backgroundColor};
          --snc-palette-background-paper2: ${backgroundColor};
          --snc-palette-background-default: ${backgroundColor};
          --MuiPaper-root-background: ${backgroundColor};
        `}
      
      ${secondaryColor &&
        css`
          --snc-palette-secondary-main: ${secondaryColor};
        `}

      body {
          ${fontColor &&
          css`
            --color-text-base: ${fontColor};
            color: ${fontColor};
          `}
          background: transparent;
        }
      `,

      root: css`
        ${primaryColor &&
        css`
          --snc-palette-primary-main: ${primaryColor};
          --snc-palette-primary-contrastText: ${pickTextColorBasedOnBgColorSimple(
            primaryColor,
            '#ffffff',
            '#000000'
          )};
        `}

        ${fontColor &&
        css`
          --snc-palette-text-primary: ${fontColor};
          --snc-palette-text-secondary: ${fontColor};
        `}

      ${backgroundColor &&
        css`
          --snc-palette-background-paper: ${backgroundColor};
          --snc-palette-background-paper2: ${backgroundColor};
          --snc-palette-background-default: ${backgroundColor};
          --MuiPaper-root-background: ${backgroundColor};
        `}
      
      ${secondaryColor &&
        css`
          --snc-palette-secondary-main: ${secondaryColor};
        `}

      ${disableLinks &&
        css`
          a,
          a * {
            pointer-events: none !important;
          }

          a.footer,
          a.footer *,
          .footer a,
          .footer a * {
            pointer-events: auto !important;
          }
        `}
      `,
    };
  }, [
    widget?.custom_css,
    backgroundColor,
    primaryColor,
    secondaryColor,
    fontColor,
    disableLinks,
  ]);

  return (
    <Box css={styles.root}>
      <Global styles={styles.globals} />
      {children}
    </Box>
  );
};
